import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import useReportLog from '../../../hooks/useReportLog';
import useGroup from '../../../zustand/useGroup';
import useAppliedReportFilter from '../../../hooks/useAppliedReportFilter';
import { httpRequest } from '../../../helpers/api';
import {
  BaseResponsePaginationProps,
  generateQueryString,
} from '@qlibs/react-components';
import {
  OperationModeDetailProperties,
  OperationModeProperties,
} from '../../operationMode/types/operationMode.type';
import { toHHMMSS } from './helperFunction';
import PageContent from './dailyReport/pageContent';
import {
  buildOperationModeSections,
  collectOperationModeDeviceIds,
  OperationModeSections,
  splitOperationModeDetails,
} from '../pdf/reportPdfData';

interface OperationModePdfSectionsProps {
  isReportSchedule?: boolean;
  interval?: string;
  startAt?: number;
  endAt?: number;
  hasNextSection?: boolean;
  // calculated by the backend for the PDF page (GET /report-log/pdf-data)
  precomputed?: OperationModeSections;
}

type SummaryRow = {
  name: string;
  runningTime: number;
  consRate: number;
};

type DetailRow = SummaryRow & {
  rpmRange: string;
  speed?: string;
};

const engineOrder = ['starboard', 'port', 'center'];

const OperationModePdfSections: React.FC<OperationModePdfSectionsProps> = (
  props
) => {
  const { logs } = useReportLog();
  const { filter, timezone } = useAppliedReportFilter();
  const [loading, setLoading] = useState(false);
  const [opModes, setOpModes] = useState<OperationModeDetailProperties[]>([]);
  const [clutchOpModes, setClutchOpModes] = useState<
    OperationModeDetailProperties[]
  >([]);

  const range = useMemo(() => {
    const isSchedule = props.isReportSchedule;
    return {
      startAt:
        isSchedule && props.startAt ? props.startAt : Number(filter.startAt),
      endAt: isSchedule && props.endAt ? props.endAt : Number(filter.endAt),
      interval:
        isSchedule && props.interval ? props.interval : String(filter.interval),
    };
  }, [props.isReportSchedule, props.startAt, props.endAt, props.interval, filter]);

  useEffect(() => {
    if (
      !props.precomputed &&
      logs &&
      logs.operation_mode &&
      Object.keys(logs.operation_mode).length
    ) {
      async function fetchOperationMode() {
        try {
          setLoading(true);
          const devicesIds = collectOperationModeDeviceIds(logs.operation_mode);

          if (!devicesIds.length) {
            setOpModes([]);
            setClutchOpModes([]);
            return;
          }

          const res = await httpRequest.get<
            BaseResponsePaginationProps<OperationModeProperties>
          >(
            `/operation-mode/data-constant/by-devices${generateQueryString({
              filterDevicesIds: devicesIds,
              limit: 100,
            })}`
          );

          const { engineDetails, clutchDetails } = splitOperationModeDetails(
            res.data.payload.results
          );

          setOpModes(engineDetails as OperationModeDetailProperties[]);
          setClutchOpModes(clutchDetails as OperationModeDetailProperties[]);
        } catch (error) {
          setOpModes([]);
          setClutchOpModes([]);
          console.error('Failed get operation mode details', error);
        } finally {
          setLoading(false);
        }
      }

      fetchOperationMode();
    }
  }, [logs, props.precomputed]);

  const sections = useMemo(
    () =>
      props.precomputed ??
      buildOperationModeSections({
        operationMode: logs?.operation_mode,
        opModes,
        clutchOpModes,
        range,
        timezone,
      }),
    [props.precomputed, logs, opModes, clutchOpModes, range, timezone]
  );
  const {
    engineKeys,
    clutchEngines,
    summaryRows,
    detailRows,
    perEngineRows,
    perEngineHasData,
    clutchSummaryRows,
    clutchDetailRows,
    perEngineClutchRows,
  } = sections;

  const renderSummaryTable = (rows: SummaryRow[]) => {
    const totalRunningTime = rows.reduce(
      (acc, curr) => acc + curr.runningTime,
      0
    );
    const totalConsRate = rows.reduce((acc, curr) => acc + curr.consRate, 0);

    return (
      <Table2>
        <thead>
          <Tr titleSection>
            <Td>ME Operating Mode</Td>
            <Td>ME Running Hour</Td>
            <Td>ME Fuel Used (Liter)</Td>
          </Tr>
        </thead>
        <tbody>
          {rows.map((item, idx) => (
            <Tr key={idx}>
              <Td>{item.name.toUpperCase()}</Td>
              <Td>{toHHMMSS(item.runningTime)}</Td>
              <Td>{item.consRate.toFixed(2)}</Td>
            </Tr>
          ))}
          <Tr titleSection>
            <Td>Total</Td>
            <Td>{toHHMMSS(totalRunningTime)}</Td>
            <Td>{totalConsRate.toFixed(2)}</Td>
          </Tr>
        </tbody>
      </Table2>
    );
  };

  const renderDetailTable = (rows: DetailRow[]) => {
    const totalRunningTime = rows.reduce(
      (acc, curr) => acc + curr.runningTime,
      0
    );
    const totalConsRate = rows.reduce((acc, curr) => acc + curr.consRate, 0);

    return (
      <Table2>
        <thead>
          <Tr titleSection>
            <Td>ME Operating Mode</Td>
            <Td>RPM Range</Td>
            <Td>Speed</Td>
            <Td>ME Running Hour</Td>
            <Td>ME Fuel Used (Liter)</Td>
          </Tr>
        </thead>
        <tbody>
          {rows.map((item, idx) => (
            <Tr key={idx}>
              <Td>{item.name}</Td>
              <Td>{item.rpmRange}</Td>
              <Td>{item.speed || '-'}</Td>
              <Td>{toHHMMSS(item.runningTime)}</Td>
              <Td>{item.consRate.toFixed(2)}</Td>
            </Tr>
          ))}
          <Tr titleSection>
            <Td>Total</Td>
            <Td>-</Td>
            <Td>-</Td>
            <Td>{toHHMMSS(totalRunningTime)}</Td>
            <Td>{totalConsRate.toFixed(2)}</Td>
          </Tr>
        </tbody>
      </Table2>
    );
  };

  const renderEngineTable = (engine: string, rows: SummaryRow[]) => {
    const label =
      engine === 'starboard'
        ? 'Starboard'
        : engine === 'port'
        ? 'Port'
        : 'Center';
    const totalRunningTime = rows.reduce(
      (acc, curr) => acc + curr.runningTime,
      0
    );
    const totalConsRate = rows.reduce((acc, curr) => acc + curr.consRate, 0);

    return (
      <Table2 key={engine}>
        <thead>
          <Tr titleSection>
            <Td>{label} Operating Mode</Td>
            <Td>{label} Running Hour</Td>
            <Td>{label} Fuel Used (Liter)</Td>
          </Tr>
        </thead>
        <tbody>
          {rows.map((item, idx) => (
            <Tr key={idx}>
              <Td>{item.name.toUpperCase()}</Td>
              <Td>{toHHMMSS(item.runningTime)}</Td>
              <Td>{item.consRate.toFixed(2)}</Td>
            </Tr>
          ))}
          <Tr titleSection>
            <Td>Total</Td>
            <Td>{toHHMMSS(totalRunningTime)}</Td>
            <Td>{totalConsRate.toFixed(2)}</Td>
          </Tr>
        </tbody>
      </Table2>
    );
  };

  const renderClutchSummaryTable = (rows: SummaryRow[]) => {
    const totalRunningTime = rows.reduce(
      (acc, curr) => acc + curr.runningTime,
      0,
    );
    const totalConsRate = rows.reduce((acc, curr) => acc + curr.consRate, 0);

    return (
      <Table2>
        <thead>
          <Tr titleSection>
            <Td>Clutch Operating Mode</Td>
            <Td>Clutch Running Hour</Td>
            <Td>Clutch Fuel Used (Liter)</Td>
          </Tr>
        </thead>
        <tbody>
          {rows.map((item, idx) => (
            <Tr key={idx}>
              <Td>{item.name.toUpperCase()}</Td>
              <Td>{toHHMMSS(item.runningTime)}</Td>
              <Td>{item.consRate.toFixed(2)}</Td>
            </Tr>
          ))}
          <Tr titleSection>
            <Td>Total</Td>
            <Td>{toHHMMSS(totalRunningTime)}</Td>
            <Td>{totalConsRate.toFixed(2)}</Td>
          </Tr>
        </tbody>
      </Table2>
    );
  };

  const renderClutchDetailTable = (
    rows: Array<SummaryRow & { clutchRange: string }>,
  ) => {
    const totalRunningTime = rows.reduce(
      (acc, curr) => acc + curr.runningTime,
      0,
    );
    const totalConsRate = rows.reduce((acc, curr) => acc + curr.consRate, 0);

    return (
      <Table2>
        <thead>
          <Tr titleSection>
            <Td>Clutch Operating Mode</Td>
            <Td>Clutch Range</Td>
            <Td>Clutch Running Hour</Td>
            <Td>Clutch Fuel Used (Liter)</Td>
          </Tr>
        </thead>
        <tbody>
          {rows.map((item, idx) => (
            <Tr key={idx}>
              <Td>{item.name}</Td>
              <Td>{item.clutchRange}</Td>
              <Td>{toHHMMSS(item.runningTime)}</Td>
              <Td>{item.consRate.toFixed(2)}</Td>
            </Tr>
          ))}
          <Tr titleSection>
            <Td>Total</Td>
            <Td>-</Td>
            <Td>{toHHMMSS(totalRunningTime)}</Td>
            <Td>{totalConsRate.toFixed(2)}</Td>
          </Tr>
        </tbody>
      </Table2>
    );
  };

  const renderClutchEngineTable = (engine: string, rows: SummaryRow[]) => {
    const label =
      engine === 'starboard'
        ? 'Starboard'
        : engine === 'port'
          ? 'Port'
          : 'Center';
    const totalRunningTime = rows.reduce(
      (acc, curr) => acc + curr.runningTime,
      0,
    );
    const totalConsRate = rows.reduce((acc, curr) => acc + curr.consRate, 0);

    return (
      <Table2 key={`${engine}-clutch`}>
        <thead>
          <Tr titleSection>
            <Td>{label} Clutch Mode</Td>
            <Td>{label} Clutch Running Hour</Td>
            <Td>{label} Clutch Fuel Used (Liter)</Td>
          </Tr>
        </thead>
        <tbody>
          {rows.map((item, idx) => (
            <Tr key={idx}>
              <Td>{item.name.toUpperCase()}</Td>
              <Td>{toHHMMSS(item.runningTime)}</Td>
              <Td>{item.consRate.toFixed(2)}</Td>
            </Tr>
          ))}
          <Tr titleSection>
            <Td>Total</Td>
            <Td>{toHHMMSS(totalRunningTime)}</Td>
            <Td>{totalConsRate.toFixed(2)}</Td>
          </Tr>
        </tbody>
      </Table2>
    );
  };

  return (
    <>
      {(engineKeys.length > 0 || clutchEngines.length === 0) && (
      <div id='operation_mode_summary'>
        <PageContent title='Daily Report Operation Mode'>
          {loading ? (
            <Table2>
              <tbody>
                <Tr>
                  <Td colSpan={3}>Loading data</Td>
                </Tr>
              </tbody>
            </Table2>
          ) : engineKeys.length > 0 ? (
            renderSummaryTable(summaryRows)
          ) : (
            <Table2>
              <tbody>
                <Tr>
                  <Td colSpan={3}>
                    Operation Mode is enabled for this asset, but no operation
                    mode data is configured.
                  </Td>
                </Tr>
              </tbody>
            </Table2>
          )}
        </PageContent>
      </div>
      )}

      {(engineKeys.length > 0 || clutchEngines.length === 0) && (
      <div id='operation_mode_detail'>
        <PageContent title='Detail Data Operation Mode'>
          {loading ? (
            <Table2>
              <tbody>
                <Tr>
                  <Td colSpan={4}>Loading data</Td>
                </Tr>
              </tbody>
            </Table2>
          ) : engineKeys.length > 0 ? (
            renderDetailTable(detailRows)
          ) : (
            <Table2>
              <tbody>
                <Tr>
                  <Td colSpan={4}>
                    Operation Mode is enabled for this asset, but no operation
                    mode data is configured.
                  </Td>
                </Tr>
              </tbody>
            </Table2>
          )}
        </PageContent>
      </div>
      )}

      {(engineKeys.length > 0 || clutchEngines.length === 0) && (
      <div id='operation_mode_per_engine'>
        {engineKeys.length > 0 && perEngineHasData ? (
          <PageContent
            title='Per Engine Operation Mode'
            pageBreak={clutchEngines.length > 0 || props.hasNextSection}
          >
            {loading ? (
              <Table2>
                <tbody>
                  <Tr>
                    <Td colSpan={3}>Loading data</Td>
                  </Tr>
                </tbody>
              </Table2>
            ) : (
              <Wrapper>
                {engineOrder
                  .filter((engine) => engineKeys.includes(engine))
                  .map((engine) =>
                    renderEngineTable(engine, perEngineRows[engine]),
                  )}
              </Wrapper>
            )}
          </PageContent>
        ) : (
          <PageContent
            title='Per Engine Operation Mode'
            pageBreak={clutchEngines.length > 0 || props.hasNextSection}
          >
            <Table2>
              <tbody>
                <Tr>
                  <Td colSpan={3}>
                    Operation Mode is enabled for this asset, but no operation mode
                    data is configured.
                  </Td>
                </Tr>
              </tbody>
            </Table2>
          </PageContent>
        )}
      </div>
      )}

      {clutchEngines.length > 0 && (
        <div id='operation_mode_clutch_summary'>
          <PageContent title='Daily Report Operation Mode Clutch'>
            {loading ? (
              <Table2>
                <tbody>
                  <Tr>
                    <Td colSpan={3}>Loading data</Td>
                  </Tr>
                </tbody>
              </Table2>
            ) : (
              renderClutchSummaryTable(clutchSummaryRows)
            )}
          </PageContent>
        </div>
      )}

      {clutchEngines.length > 0 && (
        <div id='operation_mode_clutch_detail'>
          <PageContent title='Detail Data Operation Mode Clutch'>
            {loading ? (
              <Table2>
                <tbody>
                  <Tr>
                    <Td colSpan={4}>Loading data</Td>
                  </Tr>
                </tbody>
              </Table2>
            ) : (
              renderClutchDetailTable(clutchDetailRows)
            )}
          </PageContent>
        </div>
      )}

      {clutchEngines.length > 0 && (
        <div id='operation_mode_clutch_per_engine'>
          <PageContent
            title='Per Engine Operation Mode Clutch'
            pageBreak={props.hasNextSection}
          >
            <Wrapper>
              {clutchEngines.map((engine) =>
                renderClutchEngineTable(engine, perEngineClutchRows[engine]),
              )}
            </Wrapper>
          </PageContent>
        </div>
      )}
    </>
  );
};

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const Table2 = styled.table`
  margin-top: 30px;
  width: 100%;

  thead td,
  tbody td {
    text-align: center;
  }
`;

const Tr = styled.tr<{ titleSection?: boolean }>`
  background-color: ${({ titleSection }) =>
    titleSection ? 'rgb(221, 221, 221)' : 'white'};
  font-weight: ${({ titleSection }) => (titleSection ? 'bold' : 'normal')};
`;

const Td = styled.td<{ unit?: boolean; value?: boolean }>`
  border-top: thin solid #ddd;
  border-bottom: thin solid #ddd;
  width: 10%;
  padding: 4px 8px;
  font-weight: ${({ value }) => (value ? 'bold' : 'regular')};
  color: ${({ unit }) => (unit ? 'gray' : 'black')};
`;

export default OperationModePdfSections;
